import { DotsLoader } from 'Components/DotsLoader';
import { ButtonForm, CustomTextField } from 'Components/Form/styles';
import { Column, Row } from 'Components/Grid';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useCountryConfig } from 'hooks/useCountryConfig';
import { validateTaxId, stripTaxIdFormatting } from 'utils/tax_id_validators';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { SubtitleLogin, TitleLogin } from './styles';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { createCompany, getCompanyShortBySlug } from 'services/company-service';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { MuiChipsInput } from 'mui-chips-input';
import { useTranslation } from 'react-i18next';

export const OnboardCompany = () => {
  const { t } = useTranslation(['company']);
  const { idOnboard } = useParams();
  const SwalReact = withReactContent(Swal);
  const navigate = useNavigate();
  const { data: company } = useQuery(['getCompanyBySlug'], async () => {
    const response = await getCompanyShortBySlug('vo');
    return response.data;
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({
    defaultValues: {
      name: '',
      corporateName: '',
      slug: '',
      cnpj: '',
      size: '',
      income: '',
      industry: '',
      description: '',
      active: true,
      partner: false,
      managers: [],
      roles: [],
      domains: [],
      signedPlan: '',
      country: '',
      defaultLanguage: '',
    },
  });

  const countryValue = useWatch({ control, name: 'country' });
  const { taxIdLabel, taxIdMask, taxIdPlaceholder, countryConfig, allConfigs, isError: isCountryConfigError, isLoading: isCountryConfigLoading } = useCountryConfig(countryValue);

  const mutation = useMutation(
    newCompany => {
      return createCompany(newCompany, idOnboard);
    },
    {
      onError: error => {
        // An error happened!
        SwalReact.fire({
          title: t('company:onboard.company.errorTitle'),
          text: error.response.data.message ?? error.message,
          icon: 'error',
        });
      },
      onSuccess: data => {
        if (data.status === 201) {
          const companyData = getValues();
          navigate(`/onboard-company-admin/${companyData.slug}`);
        }
      },
    },
  );

  const onSubmit = () => {
    if (!company) return;
    const companyData = getValues();
    mutation.mutate({
      name: companyData.name,
      corporateName: companyData.corporateName,
      cnpj: stripTaxIdFormatting(companyData.cnpj),
      slug: companyData.slug,
      size: companyData.size,
      income: companyData.income,
      industry: companyData.industry,
      domains: companyData.domains,
      country: companyData.country,
      defaultLanguage: companyData.defaultLanguage,
      managers: [
        {
          _id: company._id,
          name: company.name,
        },
      ],
      partner: false,
      active: true,
      signedPlan: 'Free',
      roles: [],
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Row justifyContent="center" className="mt-5">
        <Column xs={12} className="text-center mb-3">
          <TitleLogin>Virtual</TitleLogin>
          <TitleLogin className="bold"> Officer</TitleLogin>
        </Column>
        <Column xs={12} className="text-center mb-4">
          <SubtitleLogin>{t('company:onboard.company.subtitle')}</SubtitleLogin>
        </Column>
      </Row>
      {!mutation.isSuccess && (
        <>
          <Row justifyContent="center">
            <Column xs={8} sm={8} className="mx-auto mb-4" justifyContent="center" flex>
              <CustomTextField
                {...register('name', { required: t('company:onboard.company.nameRequired') })}
                label={t('company:onboard.company.name')}
                name="name"
                error={errors?.name ? true : false}
                helperText={errors?.name ? errors.name.message : null}
                InputLabelProps={{ shrink: true }}
                sx={{
                  input: {
                    padding: '16.5px 10px',
                    width: '100%',
                  },
                  '.MuiOutlinedInput-root': {
                    padding: 0,
                  },
                  width: '100%',
                }}
              />
            </Column>
          </Row>
          <Row justifyContent="center">
            <Column xs={8} sm={8} className="mx-auto mb-4" justifyContent="center" flex>
              <CustomTextField
                {...register('corporateName', { required: t('company:onboard.company.corporateNameRequired') })}
                label={t('company:onboard.company.corporateName')}
                name="corporateName"
                error={errors?.corporateName ? true : false}
                helperText={errors?.corporateName ? errors.corporateName.message : null}
                InputLabelProps={{ shrink: true }}
                sx={{
                  input: {
                    padding: '16.5px 10px',
                    width: '100%',
                  },
                  '.MuiOutlinedInput-root': {
                    padding: 0,
                  },
                  width: '100%',
                }}
              />
            </Column>
          </Row>
          <Row justifyContent="center">
            <Column xs={8} sm={4} className="ml-auto mb-4 pr-2" justifyContent="center" flex>
              <Controller
                control={control}
                name="country"
                rules={{ required: t('company:onboard.company.countryRequired') }}
                render={({ field: { onChange, value } }) => (
                  <FormControl fullWidth error={!!errors?.country || isCountryConfigError}>
                    <InputLabel shrink>{t('company:onboard.company.country')}</InputLabel>
                    <Select value={value} onChange={onChange} displayEmpty disabled={isCountryConfigError} notched label={t('company:onboard.company.country')}>
                      <MenuItem value="" disabled>
                        {isCountryConfigError ? t('company:onboard.company.countryError') : t('company:onboard.company.countryPlaceholder')}
                      </MenuItem>
                      {allConfigs.map(cc => (
                        <MenuItem key={cc.countryCode} value={cc.countryCode}>
                          {cc.name.pt}
                        </MenuItem>
                      ))}
                    </Select>
                    {isCountryConfigError && <FormHelperText>{t('company:onboard.company.countryErrorHelper')}</FormHelperText>}
                    {errors?.country && <FormHelperText>{errors.country.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            </Column>
            <Column xs={8} sm={4} className="mr-auto mb-4 pl-2" justifyContent="center" flex>
              <Controller
                control={control}
                name="defaultLanguage"
                rules={{ required: t('company:onboard.company.languageRequired') }}
                render={({ field: { onChange, value } }) => (
                  <FormControl fullWidth error={!!errors?.defaultLanguage}>
                    <InputLabel shrink>{t('company:onboard.company.language')}</InputLabel>
                    <Select value={value} onChange={onChange} displayEmpty notched label={t('company:onboard.company.language')}>
                      <MenuItem value="" disabled>{t('company:onboard.company.languagePlaceholder')}</MenuItem>
                      <MenuItem value="pt">{t('company:onboard.company.languagePt')}</MenuItem>
                      <MenuItem value="en">{t('company:onboard.company.languageEn')}</MenuItem>
                      <MenuItem value="es">{t('company:onboard.company.languageEs')}</MenuItem>
                    </Select>
                    {errors?.defaultLanguage && <FormHelperText>{errors.defaultLanguage.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            </Column>
          </Row>
          <Row justifyContent="center">
            <Column xs={8} sm={4} className="ml-auto mb-4 pr-2" justifyContent="center" flex>
              <CustomTextField
                {...register('slug', { required: t('company:onboard.company.slugRequired') })}
                label={t('company:onboard.company.slug')}
                name="slug"
                error={errors?.slug ? true : false}
                helperText={errors?.slug ? errors.slug.message : null}
                InputLabelProps={{ shrink: true }}
                sx={{
                  input: {
                    padding: '16.5px 10px',
                    width: '100%',
                  },
                  '.MuiOutlinedInput-root': {
                    padding: 0,
                  },
                  width: '100%',
                }}
              />
            </Column>
            <Column xs={8} sm={4} className="mr-auto mb-4 pl-2" justifyContent="center" flex>
              <CustomTextField
                {...register('cnpj', {
                  required: `${taxIdLabel || 'Tax ID'} é obrigatório`,
                  validate: (value) => {
                    if (!value || !countryValue) return true;
                    if (isCountryConfigLoading) return 'Aguardando configuração de país...';
                    if (!countryConfig) return true;
                    const stripped = stripTaxIdFormatting(value);
                    // Only run format regex when value has visual separators (formatted input)
                    // Skip for already-stripped DB values on edit to avoid false failures
                    if (stripped !== value && countryConfig.taxId?.regex) {
                      const formatOk = new RegExp(countryConfig.taxId.regex).test(value);
                      if (!formatOk) return `Formato inválido. Use: ${taxIdMask}`;
                    }
                    const checkOk = validateTaxId(stripped, countryValue);
                    if (!checkOk) return `${taxIdLabel} inválido. Use o formato: ${taxIdMask}`;
                    return true;
                  },
                })}
                label={taxIdLabel || 'CNPJ'}
                name="cnpj"
                placeholder={taxIdPlaceholder}
                error={errors?.cnpj ? true : false}
                helperText={errors?.cnpj ? errors.cnpj.message : null}
                InputLabelProps={{ shrink: true }}
                sx={{
                  input: {
                    padding: '16.5px 10px',
                    width: '100%',
                  },
                  '.MuiOutlinedInput-root': {
                    padding: 0,
                  },
                  width: '100%',
                }}
              />
            </Column>
          </Row>
          <Row justifyContent="center">
            <Column xs={8} sm={4} className="ml-auto mb-4 pr-2" justifyContent="center" flex>
              <CustomTextField
                {...register('size', { required: t('company:onboard.company.sizeRequired') })}
                label={t('company:onboard.company.size')}
                name="size"
                error={errors?.size ? true : false}
                helperText={errors?.size ? errors.size.message : null}
                InputLabelProps={{ shrink: true }}
                sx={{
                  input: {
                    padding: '16.5px 10px',
                    width: '100%',
                  },
                  '.MuiOutlinedInput-root': {
                    padding: 0,
                  },
                  width: '100%',
                }}
              />
            </Column>
            <Column xs={8} sm={4} className="mr-auto mb-4 pl-2" justifyContent="center" flex>
              <CustomTextField
                {...register('income', { required: t('company:onboard.company.incomeRequired') })}
                label={t('company:onboard.company.income')}
                name="income"
                error={errors?.income ? true : false}
                helperText={errors?.income ? errors.income.message : null}
                InputLabelProps={{ shrink: true }}
                sx={{
                  input: {
                    padding: '16.5px 10px',
                    width: '100%',
                  },
                  '.MuiOutlinedInput-root': {
                    padding: 0,
                  },
                  width: '100%',
                }}
              />
            </Column>
          </Row>
          <Row justifyContent="center">
            <Column xs={8} sm={8} className="mx-auto mb-4" justifyContent="center" flex>
              <CustomTextField
                {...register('industry', { required: t('company:onboard.company.industryRequired') })}
                label={t('company:onboard.company.industry')}
                name="industry"
                error={errors?.industry ? true : false}
                helperText={errors?.industry ? errors.industry.message : null}
                InputLabelProps={{ shrink: true }}
                sx={{
                  input: {
                    padding: '16.5px 10px',
                    width: '100%',
                  },
                  '.MuiOutlinedInput-root': {
                    padding: 0,
                  },
                  width: '100%',
                }}
              />
            </Column>
          </Row>
          <Row justifyContent="center">
            <Column xs={8} sm={8} className="mx-auto mb-4">
              <FormControl fullWidth>
                <Controller
                  control={control}
                  name="domains"
                  render={({ field: { onChange, value } }) => (
                    <MuiChipsInput
                      label={t('company:onboard.company.domains')}
                      value={value}
                      onChange={onChange}
                      InputLabelProps={{ shrink: true }}
                      placeholder={t('company:onboard.company.domainsPlaceholder')}
                      renderChip={(Component, props) => {
                        return (
                          <Component
                            {...props}
                            variant="outlined"
                            color="primary"
                            sx={{
                              width: 'fit-content',
                            }}
                          />
                        );
                      }}
                      sx={{
                        '& .MuiButtonBase-root.MuiChip-root.MuiChip-filled': {
                          width: 'fit-content',
                        },
                        '& .MuiButtonBase-root.MuiIconButton-root': {
                          width: 'fit-content',
                          marginTop: '0px',
                        },
                      }}
                    />
                  )}
                ></Controller>
              </FormControl>
            </Column>
          </Row>
          <Row>
            <Column xs={8} sm={8} className="mx-auto mb-4">
              <TextField
                {...register('description')}
                label={t('company:onboard.company.description')}
                multiline
                rows={4}
                sx={{ margin: '0 auto 24px', width: '100%' }}
                InputLabelProps={{ shrink: true }}
              />
            </Column>
          </Row>
          <Row justifyContent="center" className="mb-3">
            <Column xs={8} sm={6} className="mx-auto mb-2" justifyContent="center" flex>
              <ButtonForm
                className="full center"
                label={mutation.isLoading ? <DotsLoader /> : t('company:onboard.company.continue')}
                type="submit"
              />
            </Column>
          </Row>
        </>
      )}
      {/* <Row justifyContent="center" className="mb-2">
                <Column xs={12} className="text-center">
                    <HasAccount>
                        Já possui conta? <button type="button" onClick={() => navigate(`/login`)} >Login</button><br/><br/>
                    </HasAccount>
                </Column>
            </Row> */}
    </form>
  );
};
